/**
 * useDexBridge Hook — DEX Bridge Orchestration
 *
 * Handles deBridge (cross-chain + same-chain EVM/Tron) and Jupiter (same-chain Solana)
 * swap flows. Runs alongside useBridge without interfering with CCTP paths.
 *
 * Provider-specific flows:
 * - deBridge EVM: estimate → confirm → ERC-20 approve (if needed) → sign EVM tx → broadcast → poll order status
 * - deBridge Tron: estimate → confirm → TRC-20 approve → build tx via TronWeb → sign → broadcast → poll
 * - Jupiter: submitBridge (returns quote+tx) → sign Solana tx → confirm → complete
 */

import { useState, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSendTransaction, useConfig } from 'wagmi'
// `readContract` moved out with approveErc20Exact (→ @/utils/erc20Approve); it had no other caller here.
// `waitForTransactionReceipt` left with the inlined predicate (DEV-375): the receipt read now goes
// through `waitForReceiptOrUnknown`, which calls viem's action so a reverted receipt is RETURNED
// rather than thrown — the distinction @wagmi/core's wrapper destroys.
import { writeContract, switchChain } from '@wagmi/core'
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { useConnection } from '@solana/wallet-adapter-react'
import { VersionedTransaction, Transaction, PublicKey, SystemProgram } from '@solana/web3.js'
import {
  getAssociatedTokenAddress,
  getAccount,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferCheckedInstruction,
} from '@solana/spl-token'
import {
  dexApi,
  type DexEstimateResponse,
  type DexBridgeResponse,
  type RouteConfig,
  type DexOrderStatus,
} from '@/services/api'
import {
  saveDexOrder,
  updateDexOrder,
  getDexOrder,
  type StoredDexOrder,
} from '@/utils/dexOrderStorage'
import { getErrorMessage, isUserRejection, getDisplayError, getErrorTag, getErrorCodeDeep, getReportDisplayError, redactAddressesForReport, isReceiptUnknownMessage } from '@/utils/errors'
import { log } from '@/utils/log'
import { useTronWallet } from '@/hooks/useTronWallet'
import { tronWeb } from '@/config/tronweb'
import { parseUnits } from 'viem'
import { FALLBACK_TOKENS } from '@/config/fallback-tokens'
import {
  approveErc20Exact as approveErc20ExactShared,
  RECEIPT_WAIT_TIMEOUT_MS,
  receiptUnknownMessage,
  // DEV-375 — the SHARED receipt wait. `receiptTimeoutMessage` is no longer imported here: this file
  // used to carry an inlined COPY of the whole timeout-only predicate (in
  // `transferToNear1ClickDeposit`), and that duplicate is how one defect came to live at two sites.
  // The helper is exported-and-consumed rather than fixed twice, so the two can no longer diverge.
  waitForReceiptOrUnknown,
} from '@/utils/erc20Approve'
import { assertRecipientCodeAccepted, type RecipientRiskConfirm } from '@/utils/recipientCodeGuard'
import { TESTNET_EVM_CHAIN_IDS, MAINNET_EVM_CHAIN_IDS } from '@/config/chainSets'
import type { Chain } from '@/types'

const isTestnet = (import.meta.env.VITE_NETWORK || 'testnet') === 'testnet'

/**
 * Resolve wagmi chain ID from chain name (e.g. 'base' → 8453). Exported so the cancel-authorization
 * signer (useCancelOrder) binds the EIP-712 domain to the SAME chainId the backend verifier derives
 * for the order's source_chain — a mismatch would make every EVM cancel signature fail to recover.
 *
 * DERIVED from `config/chainSets.ts`, which is also what `config/wagmi.ts` and the #144 recipient-code
 * sweep build from. This used to restate all twelve ids as a literal — a third independent copy of the
 * same list, on the signature-domain path, where a drift is not a UI bug but an unverifiable
 * cancellation signature. There is one list now; `config/chainSets.test.ts` pins it on both networks.
 */
export const EVM_CHAIN_IDS: Readonly<Record<string, number>> = isTestnet
  ? TESTNET_EVM_CHAIN_IDS
  : MAINNET_EVM_CHAIN_IDS

/**
 * deBridge same-chain swap router (DeBridgeRouter). Same address on all EVM chains
 * (deBridge D10 allowlist — verified identical across chains). For same-chain swaps the
 * unsigned tx's `to` MUST equal this; we assert it before approving/sending because the
 * address is API-sourced. Cross-chain swaps use a different entrypoint (DlnSource) and are
 * intentionally exempt from this check.
 */
const DEBRIDGE_SAME_CHAIN_ROUTER = '0x663dc15d3c1ac63ff12e45ab68fea3f0a883c251'

/**
 * Cross-chain swaps route through deBridge's DLN source, or its router when the source needs a
 * pre-swap — both are the same CREATE3 address on every EVM chain. The approval spender and the
 * swap tx's `to` are API-sourced, so we assert they belong to this set before approving/sending
 * (defense-in-depth, the cross-chain analogue of DEBRIDGE_SAME_CHAIN_ROUTER).
 */
const DEBRIDGE_CROSS_CHAIN_SPENDERS = new Set<string>([
  '0xef4fb24ad0916217251f553c0596f8edc630eb66', // DlnSource
  '0x663dc15d3c1ac63ff12e45ab68fea3f0a883c251', // DeBridgeRouter (source pre-swap)
])

/** Same-chain aggregator quotes are short-lived; rebuild if older than this before sending. */
const SAME_CHAIN_QUOTE_STALE_MS = 30_000

/**
 * Detect an empty "execution reverted" (0x, no reason string). The DeBridgeRouter swallows
 * the inner aggregator's ReturnAmountIsNotEnough (a stale-quote/price-drift trip of the min)
 * into a bare revert(), so a same-chain swap surfaces as a reasonless revert. Keyed on the
 * empty-revert shape, NOT on any specific aggregator (1inch/0x/odos vary per request).
 *
 * ⚠ DRIFT GUARD: this is the PRODUCTION copy of the empty-revert classifier. The Tier-3 fork
 * harness mirrors it byte-for-byte in apps/api/tests/support/empty-revert.ts. The SAME golden
 * cases are asserted on both: apps/web/src/hooks/emptyRevert.test.ts (this copy) and
 * apps/api/tests/dex/empty-revert.test.ts (the harness copy). Edit one → mirror the other, or one
 * of those suites turns RED. Exported solely so the drift-guard test can exercise it.
 */
export function isEmptyRevert(error: unknown): boolean {
  const msg = getErrorMessage(error).toLowerCase()
  if (!msg.includes('execution reverted') && !msg.includes('reverted for an unknown reason')) {
    return false
  }
  // A revert carrying a decoded reason string is NOT this case (don't blindly retry those).
  return !/reverted(:| with)\s+\S/i.test(getErrorMessage(error))
}

export type DexBridgeStep =
  | 'idle'
  | 'fetching_quote'
  | 'confirming_route'
  | 'approving'
  | 'signing'
  | 'broadcasting'
  | 'polling'
  | 'complete'
  | 'error'

export interface DexBridgeParams {
  sourceChain: string
  destChain: string
  sourceToken: string
  destToken: string
  amount: string
  recipientAddress: string
  senderAddress: string
  /** Advisory native balance (wei string) for the backend's on-click affordability check. */
  senderNativeBalanceWei?: string
  /** Advisory source-token balance (atomic) for the backend's cross-chain ERC-20 opex gate. */
  senderTokenBalanceAtomic?: string
  route: RouteConfig
  /**
   * #144 (R2). The DEX path is NOT outside the fix set: `BridgePage` feeds `executeDexBridge` the
   * SAME derived recipient the CCTP burn uses, and it becomes deBridge's payout target. A Safe that
   * exists only on the source chain, pasted as a DEX payout, loses funds identically — and a guard
   * scoped "before the burn" would never run here. Required for the same reason as on BurnParams.
   */
  confirmRecipientRisk: RecipientRiskConfirm
}

export interface DexBridgeState {
  currentStep: DexBridgeStep
  transferId: string | null
  orderId: string | null
  sourceTxHash: string | null
  estimate: DexEstimateResponse | null
  bridgeResponse: DexBridgeResponse | null
  orderStatus: DexOrderStatus | null
  error: string | null
  /**
   * #261 F9 — the phase this swap was in when it failed, kept ALONGSIDE `currentStep: 'error'`
   * instead of being overwritten by it. Every terminal-failure writer below records it, so the
   * progress card can red only the step that actually failed rather than all three. `null` while the
   * swap is healthy, and cleared by `reset()` because it lives in `initialState` (I-7).
   *
   * Same vocabulary as `currentStep` (`DexBridgeStep` ⊇ `DEX_ORDER` in utils/steps.ts), which is what
   * lets `makeDexSteps` gate on it with no translation table.
   */
  failedStep: DexBridgeStep | null
  /**
   * DEV-375 finding #1 — WHICH leg failed, independent of `failedStep` (which phase). A downstream
   * failure (deBridge `OrderCancelled`, 1Click `REFUNDED`/`FAILED`/`INCOMPLETE_DEPOSIT`) means the
   * SOURCE transaction SUCCEEDED — only the backend-tracked fill/settlement failed. `failedPhase`
   * alone cannot express this: it collapses receipt-unknown, a genuine source-side revert, AND a
   * downstream refund all to the same 'polling' phase, so the source row rendered a false red X for
   * a transaction that landed. `null` for a receipt-unknown (the source-vs-downstream question does
   * not apply — I10 already renders it neutral) and while the swap is healthy; cleared by `reset()`
   * because it lives in `initialState` (I-7).
   */
  failedLeg: 'source' | 'downstream' | null
  /**
   * The SOURCE amount the user submitted (human units). Captured at submit so the progress card and
   * Activity row always have it — the shared `bridgeState.amount` input is cleared by reset() when the
   * DEX flow starts (BridgePage), which otherwise makes long 1Click fills render "0" (#94 Bug 4 parity).
   */
  amount: string
}

const initialState: DexBridgeState = {
  currentStep: 'idle',
  transferId: null,
  orderId: null,
  sourceTxHash: null,
  estimate: null,
  bridgeResponse: null,
  orderStatus: null,
  error: null,
  failedStep: null,
  failedLeg: null,
  amount: '',
}

// TRC-20 token addresses on Tron
const TRON_TOKEN_ADDRESSES: Record<string, string> = {
  USDT: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
  USDC: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8',
}

// Address-format regexes for the 1Click deposit-address guardrail (mirror the backend).
const FE_EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const FE_SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
const FE_TRON_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/

/**
 * Client-side guardrail: validate a 1Click deposit address against the SOURCE chain's format
 * BEFORE instructing any transfer. The user funds this address directly (no on-chain contract
 * guard), so refusing a malformed/wrong-chain address here is the first line of defense.
 */
// Per-family zero-address SENTINELS (#599). Each is FORMAT-valid — the regexes above pass it — and a
// plain transfer to any of them is an unrecoverable burn. Evaluated against the 1Click DEPOSIT address
// ONLY (this function has exactly one call site): the Tron one is ALSO the registry address of native
// TRX, so it must never be compared against a token address.
const FE_DEPOSIT_SENTINEL_EVM = '0x0000000000000000000000000000000000000000'
const FE_DEPOSIT_SENTINEL_SOLANA = '11111111111111111111111111111111' // System Program id
const FE_DEPOSIT_SENTINEL_TRON = 'T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb'  // base58(41 + 20 zero bytes)
export function isValidDepositAddress(address: string, sourceChain: string): boolean {
  if (!address) return false
  if (sourceChain === 'tron') return address !== FE_DEPOSIT_SENTINEL_TRON && FE_TRON_ADDRESS_RE.test(address)
  if (sourceChain.includes('solana')) return address !== FE_DEPOSIT_SENTINEL_SOLANA && FE_SOLANA_ADDRESS_RE.test(address)
  return address.toLowerCase() !== FE_DEPOSIT_SENTINEL_EVM && FE_EVM_ADDRESS_RE.test(address)
}

/** Convert 0x-prefixed hex address to Tron hex41 format */
function toTronHex41(address: string): string {
  if (address.startsWith('0x')) {
    return `41${address.slice(2)}`
  }
  return tronWeb.address.toHex(address)
}

/** Strip 0x prefix from hex string */
function clipHexPrefix(hex: string): string {
  return hex.startsWith('0x') ? hex.slice(2) : hex
}

/**
 * Terminal-failure copy for a swap that did not complete.
 *
 * MUST NOT assert that funds were refunded, and MUST NOT advise a blind retry. The previous wording
 * ("Any debited funds are refunded to your source wallet; you can safely retry.") stated both as fact.
 * Both were FALSE for a real Coinbase Smart Wallet swap on Base that actually SUCCEEDED on-chain and
 * was only misreported as failed by a backend ownership check: nothing had been refunded, and
 * "safely retry" invited the user to execute a SECOND real swap. The frontend cannot know whether a
 * refund occurred — only the explorer can — so it hedges and points there instead of promising.
 *
 * Kept deliberately calm: the common case really is a genuine cancellation where funds do return.
 * Single shared constant because this text renders from two places (the in-session poll and the
 * background reconcile) which must never drift apart.
 */
export const DEX_TERMINAL_FAILURE_MESSAGE =
  'Swap did not complete — the order failed or was cancelled. Funds are usually returned to your source wallet automatically, but check your source transaction in the explorer to confirm before retrying.'

export function useDexBridge() {
  const [state, setState] = useState<DexBridgeState>(initialState)
  const stateRef = useRef(state)
  stateRef.current = state

  // Wagmi EVM send + config for contract reads/writes
  const { sendTransactionAsync } = useSendTransaction()
  const wagmiConfig = useConfig()

  // Solana wallet adapter
  const { signTransaction: solanaSignTransaction } = useSolanaWallet()
  const { connection: solanaConnection } = useConnection()

  // Tron wallet adapter
  const { signTronTransaction, tronAddress } = useTronWallet()

  // Order polling
  const { data: polledStatus } = useQuery({
    queryKey: ['dex-order-status', state.transferId],
    queryFn: async () => {
      if (!state.transferId) return null
      const res = await dexApi.getOrderStatus(state.transferId)
      if (res.success && res.data) {
        setState(prev => ({ ...prev, orderStatus: res.data ?? null }))
        // Check terminal states
        const s = res.data.state?.toLowerCase()
        if (s === 'fulfilled' || s === 'claimedunlock') {
          setState(prev => {
            // Terminal transitions are only ever made FROM a live 'polling' state, and terminal states
            // are STICKY (see applyReconciledTerminal, which uses the same guard). Without this, an
            // in-flight poll request that started while polling but resolves AFTER the durable-reconcile
            // loop (or an earlier poll) already terminalized this transfer would overwrite the settled
            // state — the reported "flash Transfer Complete → regress to Moving between chains". No-op
            // once already terminal.
            if (prev.currentStep !== 'polling') return prev
            if (prev.transferId) {
              updateDexOrder(prev.transferId, { orderStatus: 'complete' })
            }
            return { ...prev, currentStep: 'complete' }
          })
        } else if (s === 'ordercancelled' || s === 'claimedordercancel') {
          // Terminal failure (the backend also maps a dropped/never-created source tx to this state).
          // Stop the spinner and surface a retryable error instead of "Tracking order" forever.
          setState(prev => {
            // Same monotonic guard as the 'complete' branch above: NEVER regress a transfer that has
            // already reached a terminal state. A late/stale in-flight cancel read must not un-complete a
            // transfer the reconcile loop (or an earlier poll) already flipped to 'complete'.
            if (prev.currentStep !== 'polling') return prev
            if (prev.transferId) {
              // #261 F9 site 1. `failedStep` is persisted here as well as written to state: this is
              // the DOMINANT same-chain failure path, and without the stored copy the very same
              // order reopened from Activity would carry `orderStatus:'failed'` with neither an
              // `error` string (this writer has never set one) nor a phase — which makeDexSteps can
              // only render as ALL THREE rows failed, including the quote and the broadcast, both of
              // which demonstrably succeeded (I-1). That fallback is loud and honest rather than
              // wrong-and-green, but it is still the degraded render, and this write is what avoids it.
              updateDexOrder(prev.transferId, { orderStatus: 'failed', failedStep: 'polling' })
            }
            return {
              ...prev,
              currentStep: 'error',
              error: DEX_TERMINAL_FAILURE_MESSAGE,
              // The order was being POLLED when the backend reported it cancelled: the quote and the
              // signature/broadcast both succeeded, so only the tracking row failed.
              failedStep: 'polling',
              // DEV-375 finding #1 — this is a BACKEND-reported cancel (deBridge `OrderCancelled` /
              // `ClaimedOrderCancel`), not a client-side broadcast/receipt failure: the source leg
              // SUCCEEDED. `crossChainStep` routes this to 'attesting' (source row stays done), not
              // 'confirming' (which would red the source row for a transaction that landed).
              failedLeg: 'downstream',
            }
          })
        }
        return res.data
      }
      return null
    },
    refetchInterval: 5000,
    // Keep polling while the tab is backgrounded — 1Click cross-chain fills take minutes, and if the poll
    // pauses the terminal 'complete' transition never fires in-session (the card sticks on "polling" and the
    // post-swap balance/activity refresh never runs). Background reconcile is only the fallback.
    refetchIntervalInBackground: true,
    enabled: !!state.transferId && state.currentStep === 'polling',
  })

  // Keep polledStatus in sync (used by consumers)
  void polledStatus

  const persistOrder = useCallback((params: DexBridgeParams, transferId: string, orderId?: string): void => {
    const order: StoredDexOrder = {
      transferId,
      orderId,
      sourceChain: params.sourceChain,
      destinationChain: params.destChain,
      sourceAddress: params.senderAddress,
      destinationAddress: params.recipientAddress,
      sourceToken: params.sourceToken,
      destToken: params.destToken,
      amount: params.amount,
      provider: params.route.provider,
      orderStatus: 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    saveDexOrder(order)
  }, [])

  const executeDexBridge = useCallback(
    async (params: DexBridgeParams): Promise<string> => {
      const { route } = params

      // ── #144 GUARD — the DEX payout target ────────────────────────────────────────────────────
      // Same shared predicate the three CCTP signing paths call, against the exact recipient that
      // becomes deBridge's payout address. Solana and Tron destinations return `ok` with zero RPC —
      // `destChain` is a plain string here, and the guard treats anything that is not one of the six
      // configured EVM chains as a clean skip, so an unrecognised value can never crash a route.
      //
      // Placed BEFORE the setState below, deliberately: a declined warning must leave the UI exactly
      // as it was, not stranded on a "fetching quote" panel for a transfer that will never start.
      await assertRecipientCodeAccepted({
        recipient: params.recipientAddress,
        destChain: params.destChain as Chain,
        confirm: params.confirmRecipientRisk,
      })

      setState({
        ...initialState,
        currentStep: 'fetching_quote',
        // Snapshot the submitted amount so the progress card / Activity row survive the live-input clear.
        amount: params.amount,
      })

      // Track transferId locally so the catch block can update the order in
      // localStorage even if the React state hasn't re-rendered yet.
      let localTransferId: string | null = null
      // #261 F9 — the same trick for the source tx hash, and for the same reason: assigned
      // SYNCHRONOUSLY at each broadcast point, so the catch below can tell "failed before the money
      // moved" from "failed after it did" without depending on React having committed a render.
      let localSourceTxHash: string | null = null

      try {
        // ── Jupiter flow ──────────────────────────────────────
        if (route.provider === 'jupiter') {
          // Skip getEstimate — go straight to submitBridge
          setState(prev => ({ ...prev, currentStep: 'fetching_quote' }))

          const jupiterPayload = {
            sourceChain: params.sourceChain,
            destChain: params.destChain,
            sourceToken: params.sourceToken,
            destToken: params.destToken,
            amount: params.amount,
            recipientAddress: params.recipientAddress,
            senderAddress: params.senderAddress,
            // Forward the referral code so a first-time referee's swap attributes + pays the referrer
            // on-chain (backend `?ref` fallback). Submit-only — never sent on estimate. Mirrors useBridge.ts:274.
            ref: localStorage.getItem('parsebridge:ref') || undefined,
          }

          // A user swap is HIGH priority server-side, but an extreme burst can still return a retryable
          // "busy" (local token-bucket throttle / Jupiter 429 — code BUSY_RETRYABLE). Auto-retry with
          // exponential backoff (400/800/1600ms) before surfacing any error — a transient local throttle
          // must NOT show the user a hard failure. Non-retryable errors break out immediately.
          const JUPITER_MAX_RETRIES = 3
          let bridgeRes = await dexApi.submitBridge(jupiterPayload)
          for (
            let attempt = 1;
            attempt <= JUPITER_MAX_RETRIES && !bridgeRes.success && bridgeRes.error?.code === 'BUSY_RETRYABLE';
            attempt++
          ) {
            await new Promise(resolve => setTimeout(resolve, 400 * 2 ** (attempt - 1)))
            bridgeRes = await dexApi.submitBridge(jupiterPayload)
          }

          if (!bridgeRes.success || !bridgeRes.data) {
            throw new Error(bridgeRes.error?.message || 'Failed to get Jupiter swap')
          }

          const bridgeData = bridgeRes.data
          localTransferId = bridgeData.transferId

          setState(prev => ({
            ...prev,
            transferId: bridgeData.transferId,
            bridgeResponse: bridgeData,
            currentStep: 'signing',
          }))

          // Deserialize and sign Solana transaction
          const swapTx = bridgeData.tx?.swapTransaction as string | undefined
          if (!swapTx || !solanaSignTransaction) {
            throw new Error('Solana wallet not connected or no swap transaction')
          }

          const txBuffer = Buffer.from(swapTx, 'base64')
          const versionedTx = VersionedTransaction.deserialize(txBuffer)

          // Refresh the recent blockhash right before signing so Jupiter's blockhash can't expire while
          // the wallet's confirm popup is open (a stale blockhash makes the wallet simulate a stale tx
          // and show "reverted during simulation"). BUT: if the backend already PARTIALLY SIGNED the tx
          // (the jupiter-splitter co-signs the split() instruction), replacing the blockhash would VOID
          // that signature — it is over the original message — so the RPC preflight then fails signature
          // verification ("would fail on-chain"). Only refresh when there is NO backend co-signature; a
          // co-signed tx keeps the backend's (already-fresh) blockhash as-is.
          const hasBackendSignature = versionedTx.signatures.some(sig => sig.some(b => b !== 0))
          if (!hasBackendSignature) {
            try {
              const { blockhash } = await solanaConnection.getLatestBlockhash('confirmed')
              versionedTx.message.recentBlockhash = blockhash
            } catch {
              // Non-fatal — proceed with Jupiter's original blockhash.
            }
          }

          const signedTx = await solanaSignTransaction(versionedTx)

          setState(prev => ({ ...prev, currentStep: 'broadcasting' }))

          // Send transaction
          const rawTx = signedTx.serialize()
          let txHash: string
          try {
            txHash = await solanaConnection.sendRawTransaction(rawTx, {
              skipPreflight: false,
              maxRetries: 3,
            })
          } catch (sendErr) {
            // #301 item 2 — INSTRUMENTATION ONLY, console-only, no behaviour change.
            //
            // `skipPreflight: false` means the RPC SIMULATES before accepting, and on a preflight
            // failure web3.js throws `SendTransactionError({ action: 'simulate', signature: '',
            // transactionMessage: res.error.message, logs: res.error.data.logs })`. Those `logs` are
            // the program-level trace — the only artifact that names WHICH program and WHICH error
            // code rejected the swap. They reach `errors.ts`'s classifier as match text (getErrorLogs)
            // but the raw array is never printed anywhere, so a live failure leaves nothing readable.
            //
            // `transactionError` is the PUBLIC accessor (`index.d.ts:3810-3813`) returning
            // `{ message, logs? }`. Do NOT read `transactionMessage`: it is declared `private` at
            // `:3802` and an `instanceof SendTransactionError` narrowing then fails `tsc`.
            //
            // NEVER call `getLogs()` — the library's own error text instructs you to, and it is a trap
            // here: it fires a doomed RPC (`signature` is hardcoded `''`, `index.cjs.js:8210`) AND
            // replaces `transactionLogs` with a Promise (`:2203-2215`), after which the `.logs` getter
            // returns `undefined` forever, breaking the downstream classification at `errors.ts:228`.
            //
            // The whole record is wrapped so instrumentation can never mask the real failure, and the
            // ORIGINAL error object is re-thrown unmodified — the outer catch's classification, the
            // user-facing copy, and the client-error report are all byte-identical to before.
            try {
              const revert = (sendErr as { transactionError?: { message?: unknown; logs?: unknown } } | null)
                ?.transactionError
              // #303 — ALWAYS fires (log.error is not PROD-gated): the program-level revert trace is
              // the only readable artifact of a live Solana broadcast failure and feeds the error surface.
              log.error('SOLANA_SEND_REVERT', {
                transferId: bridgeData.transferId,
                sourceChain: params.sourceChain,
                destChain: params.destChain,
                sourceToken: params.sourceToken,
                destToken: params.destToken,
                message: typeof revert?.message === 'string'
                  ? revert.message
                  : getErrorMessage(sendErr),
                // `logs` is `undefined` whenever the RPC error carried no `data` (`:8204`), so this is
                // null on that path rather than a missing key.
                logs: Array.isArray(revert?.logs) ? revert.logs : null,
              })
            } catch {
              // Instrumentation must never change the outcome of a broadcast.
            }
            throw sendErr
          }

          // Persist order only after broadcast — matches CCTP pattern where
          // transfers only appear in Activity after on-chain commitment.
          persistOrder(params, bridgeData.transferId, bridgeData.orderId)

          localSourceTxHash = txHash
          setState(prev => ({
            ...prev,
            sourceTxHash: txHash,
          }))

          updateDexOrder(bridgeData.transferId, {
            sourceTxHash: txHash,
            orderStatus: 'signed',
          })

          // Confirm on-chain. DEV-375 three-way: err == null ⇒ SUCCESS, err != null ⇒ FAILED,
          // threw ⇒ UNKNOWN. This used to discard the result entirely, so a Jupiter swap that failed
          // on chain still marched on to `currentStep: 'complete'`.
          await confirmSolanaOrUnknown(txHash, 'swap')

          updateDexOrder(bridgeData.transferId, { orderStatus: 'confirmed' })

          // Confirm with backend
          await dexApi.confirmBridge(bridgeData.transferId, txHash)

          // Jupiter same-chain: tx confirmation = complete
          setState(prev => ({
            ...prev,
            currentStep: 'complete',
          }))

          updateDexOrder(bridgeData.transferId, { orderStatus: 'complete' })

          return txHash
        }

        // ── NEAR 1Click flow (Tron corridor, Phase A — deposit-address model) ──
        // No estimate/calldata/approval: the backend returns a deposit address + quoted out/min-out,
        // the user TRANSFERS the input to it (plain token transfer), then we poll for the solver fill.
        if (route.provider === 'near_1click') {
          setState(prev => ({ ...prev, currentStep: 'fetching_quote' }))

          const bridgeRes = await dexApi.submitBridge({
            sourceChain: params.sourceChain,
            destChain: params.destChain,
            sourceToken: params.sourceToken,
            destToken: params.destToken,
            amount: params.amount,
            recipientAddress: params.recipientAddress,
            senderAddress: params.senderAddress,
          })
          if (!bridgeRes.success || !bridgeRes.data) {
            // Surfaces backend guardrail messages (min-amount, quote deviation, "USDC on Tron coming soon").
            throw new Error(bridgeRes.error?.message || 'Failed to get 1Click quote')
          }
          const bridgeData = bridgeRes.data
          localTransferId = bridgeData.transferId

          const depositAddress = bridgeData.depositAddress
          if (!depositAddress) {
            throw new Error('1Click did not return a deposit address')
          }
          // GUARDRAIL: never instruct a send to an empty/malformed/wrong-chain address.
          if (!isValidDepositAddress(depositAddress, params.sourceChain)) {
            throw new Error('Refusing to deposit — provider returned an unexpected address for this chain.')
          }

          setState(prev => ({
            ...prev,
            transferId: bridgeData.transferId,
            bridgeResponse: bridgeData,
            currentStep: 'signing',
          }))

          // DEV-375 F5 — ALL FOUR post-broadcast writes, in ONE synchronous block, fired the moment
          // a hash exists rather than after the whole deposit helper returns. Matches the exemplars
          // at the Jupiter and deBridge-Solana branches above.
          //
          // A HALF-HOIST SELF-DELETES, so this is not four independent statements that happen to sit
          // together: `persistOrder` alone writes `orderStatus:'pending'` with NO hash, and
          // `getAllActiveDexOrders` `localStorage.removeItem`s every `pending && !sourceTxHash` row
          // — on mount, on the 15s reconcile tick, and on every Activity render
          // (`dexOrderStorage.ts:162-167`). Persist without the `updateDexOrder` beside it and the
          // row is gone within ~15s, taking the hash with it. Keep them together.
          const onBroadcast = (h: string) => {
            // DEV-375 finding #4 — this is called TWICE by design (see below), and was falsely
            // idempotent: `persistOrder` writes a FRESH row (orderStatus:'pending', createdAt:
            // Date.now(), no orderId/destTxHash/error) even when `h` is unchanged. If a 1Click fill
            // lands while we're still awaiting the receipt, `reconcile` writes 'complete' +
            // destTxHash in between the two calls, and this second call would wipe it back to
            // pending/polling — dropping destTxHash and resetting createdAt. Skip entirely when
            // nothing changed.
            if (h === localSourceTxHash) return
            localSourceTxHash = h
            setState(prev => ({ ...prev, sourceTxHash: h }))
            persistOrder(params, bridgeData.transferId)
            updateDexOrder(bridgeData.transferId, { sourceTxHash: h, orderStatus: 'polling' })
          }

          // Transfer the INPUT to the deposit address (no approve, no calldata). depositMemo is for
          // memo-chains only (not EVM/Sol/Tron) — passed through where applicable.
          const txHash = await transferToNear1ClickDeposit(params, depositAddress, bridgeData.depositMemo ?? null, onBroadcast)

          // Tron fires no onBroadcast (no post-broadcast read on that branch), and a repriced EVM
          // transaction returns the REPLACEMENT's hash (I9) — in both cases the value written above
          // may not be the final one, so re-assert with what the helper actually returned.
          // `onBroadcast`'s own early-return makes this call a true no-op when the hash already
          // matches, rather than a second write that happens to look the same.
          onBroadcast(txHash)

          // Poll our track endpoint — the order-poller advances the 1Click status server-side and the
          // GET /order/:id endpoint maps it into the state vocabulary this hook already interprets.
          setState(prev => ({ ...prev, currentStep: 'polling' }))
          return txHash
        }

        // ── deBridge flow ─────────────────────────────────────
        // Step 1: Get fee estimate
        const estimateRes = await dexApi.getEstimate({
          sourceChain: params.sourceChain,
          destChain: params.destChain,
          sourceToken: params.sourceToken,
          destToken: params.destToken,
          amount: params.amount,
        })

        let estimate: DexEstimateResponse | null = null
        if (estimateRes.success && estimateRes.data) {
          estimate = estimateRes.data
          setState(prev => ({ ...prev, estimate }))
        }
        // Non-fatal if estimate fails — proceed with bridge

        // Step 2: Fee delta check
        if (estimate) {
          const delta = Math.abs(estimate.estimatedAllInBps - route.affiliateFeeBps)
          if (delta > 15) {
            setState(prev => ({ ...prev, currentStep: 'confirming_route', estimate }))
            // In a real flow, the UI would prompt the user to confirm.
            // For MVP, we auto-proceed after setting the state.
          }
        }

        // Step 3: Submit bridge to get unsigned tx
        setState(prev => ({ ...prev, currentStep: 'fetching_quote' }))

        const bridgeRes = await dexApi.submitBridge({
          sourceChain: params.sourceChain,
          destChain: params.destChain,
          sourceToken: params.sourceToken,
          destToken: params.destToken,
          amount: params.amount,
          recipientAddress: params.recipientAddress,
          senderAddress: params.senderAddress,
          senderNativeBalance: params.senderNativeBalanceWei,
          senderTokenBalance: params.senderTokenBalanceAtomic,
          // Referral fallback for a first-time referee (submit-only; never on estimate). Mirrors useBridge.ts:274.
          ref: localStorage.getItem('parsebridge:ref') || undefined,
        })

        if (!bridgeRes.success || !bridgeRes.data) {
          // INSUFFICIENT_NATIVE_FOR_FEES surfaces here as a CTA-style "Need ~X SYM for fees + gas"
          // message from the backend's authoritative click-time check.
          throw new Error(bridgeRes.error?.message || 'Failed to create bridge transaction')
        }

        // Mutable: a first-time cross-chain ERC-20 source comes back as a needsApproval directive
        // (no swap tx); after the one-time approval we re-submit and replace this with the real tx.
        let bridgeData = bridgeRes.data
        localTransferId = bridgeData.transferId
        // When the (same-chain) quote was built. Used to detect staleness before sending —
        // the embedded aggregator min is short-lived and reverts if price drifts past it.
        let quoteBuiltAt = Date.now()

        setState(prev => ({
          ...prev,
          transferId: bridgeData.transferId,
          orderId: bridgeData.orderId ?? null,
          bridgeResponse: bridgeData,
          currentStep: 'signing',
        }))

        // ── Tron signing path ───────────────────────────────
        if (params.sourceChain === 'tron') {
          const txHash = await signAndBroadcastTron(params, bridgeData)
          return txHash
        }

        // ── Solana-source cross-chain (DLN) signing path ─────
        // deBridge returns a Solana tx (bridgeData.tx.solanaTransaction, base64) plus a DLN orderId.
        // Sign with the Solana wallet (reuse the Jupiter deserialize → sign → send → confirm pieces),
        // then poll the order like any cross-chain swap — it is a DLN order with an orderId, NOT a
        // same-chain "complete". No SPL approval step (the transfer is self-contained).
        if (params.sourceChain === 'solana') {
          const solTx = bridgeData.tx?.solanaTransaction as string | undefined
          if (!solTx || !solanaSignTransaction) {
            throw new Error('Solana wallet not connected or no transaction')
          }
          const versionedTx = VersionedTransaction.deserialize(Buffer.from(solTx, 'base64'))
          // Refresh the recent blockhash right before signing — deBridge's can expire while the wallet
          // confirm popup is open (stale-tx "reverted in simulation"). deBridge's Solana tx is user-only
          // signed today; guard anyway so a future backend co-signature isn't voided by the refresh
          // (changing the blockhash invalidates any pre-existing signature over the original message).
          const hasBackendSignature = versionedTx.signatures.some(sig => sig.some(b => b !== 0))
          if (!hasBackendSignature) {
            try {
              const { blockhash } = await solanaConnection.getLatestBlockhash('confirmed')
              versionedTx.message.recentBlockhash = blockhash
            } catch {
              // Non-fatal — proceed with deBridge's original blockhash.
            }
          }
          const signedTx = await solanaSignTransaction(versionedTx)

          setState(prev => ({ ...prev, currentStep: 'broadcasting' }))
          const txHash = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            maxRetries: 3,
          })

          // Persist + confirm only after broadcast (matches the CCTP/Jupiter pattern).
          persistOrder(params, bridgeData.transferId, bridgeData.orderId)
          localSourceTxHash = txHash
          setState(prev => ({ ...prev, transferId: bridgeData.transferId, sourceTxHash: txHash }))
          updateDexOrder(bridgeData.transferId, { sourceTxHash: txHash, orderStatus: 'signed' })

          // DEV-375 three-way — same rule as the Jupiter branch above.
          await confirmSolanaOrUnknown(txHash, 'swap')
          await dexApi.confirmBridge(bridgeData.transferId, txHash)

          // Cross-chain DLN order → poll for fill (unlike Jupiter same-chain which completes on confirm).
          updateDexOrder(bridgeData.transferId, { orderStatus: 'polling' })
          setState(prev => ({ ...prev, currentStep: 'polling' }))
          return txHash
        }

        // ── EVM signing path ────────────────────────────────
        // Token/chain info — hoisted because both the first-time approval (B3) and the swap-tx
        // approval below need it.
        const evmChainId = EVM_CHAIN_IDS[params.sourceChain]
        const tokenInfo = FALLBACK_TOKENS.find(
          t => t.symbol === params.sourceToken && t.chainName === params.sourceChain
        )
        const isNativeToken = !tokenInfo || tokenInfo.address === '0x0000000000000000000000000000000000000000'

        // Allowlists for the API-sourced approval spender / swap target. Same-chain MUST be the
        // DeBridgeRouter; cross-chain MUST be the DLN source or router (both fixed per chain).
        const assertSameChainRouter = (to: string) => {
          if (to.toLowerCase() !== DEBRIDGE_SAME_CHAIN_ROUTER) {
            throw new Error('Unexpected swap router address — refusing to sign for safety.')
          }
        }
        const assertCrossChainSpender = (to: string) => {
          if (!DEBRIDGE_CROSS_CHAIN_SPENDERS.has(to.toLowerCase())) {
            throw new Error('Unexpected cross-chain router address — refusing to sign for safety.')
          }
        }

        // Bounded ERC-20 approval with SafeERC20.forceApprove semantics: approve EXACTLY `target`
        // (never MAX); reset-to-0 first for USDT-class tokens that revert on approve-over-nonzero.
        // No-op when the allowance already covers `target`. Verifies the post-allowance.
        //
        // The implementation MOVED to `@/utils/erc20Approve` (#137 Phase 1) so the CCTP `bridge()`
        // path reuses this exact, already-hardened approve rather than growing a second one. Nothing
        // about its behaviour changed: the two closure captures became parameters (`params.senderAddress`
        // → `owner`, the `currentStep: 'approving'` write → the `onApproving` callback below).
        const approveErc20Exact = (tokenAddr: `0x${string}`, spender: `0x${string}`, target: bigint, chainId: number): Promise<void> =>
          approveErc20ExactShared(
            wagmiConfig,
            tokenAddr,
            spender,
            params.senderAddress as `0x${string}`,
            target,
            chainId,
            () => setState(prev => ({ ...prev, currentStep: 'approving' })),
          )

        // ── B3: first-time cross-chain ERC-20 approval ───────
        // deBridge returns an allowance directive (no swap tx) until its DLN router can transferFrom.
        // Approve what the directive says to the allowlisted allowanceTarget, then re-submit to fetch the
        // real swap tx. For a plain B3 directive that is deBridge's exact allowanceValue; for a
        // PRE_APPROVAL_REVERT directive (the pre-sign simulation reverted below allowance) it is the give
        // PLUS an operating-expense headroom (round-2 N2), so ordinary opex drift on the re-submit does not
        // land a second directive — a successful swap can therefore leave a small residual allowance.
        // (Cross-chain by construction — same-chain never returns needsApproval.)
        if (bridgeData.needsApproval) {
          if (!evmChainId || !tokenInfo || isNativeToken || !bridgeData.allowanceTarget) {
            throw new Error('Approval required but token/route info is missing — re-initiate the swap.')
          }
          const spender = bridgeData.allowanceTarget as `0x${string}`
          assertCrossChainSpender(spender)
          const target = bridgeData.approvalAmount
            ? BigInt(bridgeData.approvalAmount)
            : (bridgeData.allowanceValue ? BigInt(bridgeData.allowanceValue) : parseUnits(params.amount, tokenInfo.decimals ?? 6))
          await switchChain(wagmiConfig, { chainId: evmChainId })
          await approveErc20Exact(tokenInfo.address as `0x${string}`, spender, target, evmChainId)

          // Re-submit now that the allowance exists → deBridge returns the real swap tx.
          setState(prev => ({ ...prev, currentStep: 'fetching_quote' }))
          const resub = await dexApi.submitBridge({
            sourceChain: params.sourceChain,
            destChain: params.destChain,
            sourceToken: params.sourceToken,
            destToken: params.destToken,
            amount: params.amount,
            recipientAddress: params.recipientAddress,
            senderAddress: params.senderAddress,
            senderNativeBalance: params.senderNativeBalanceWei,
            senderTokenBalance: params.senderTokenBalanceAtomic,
            ref: localStorage.getItem('parsebridge:ref') || undefined,
          })
          if (!resub.success || !resub.data || resub.data.needsApproval) {
            throw new Error(resub.error?.message || 'Approval did not register with the bridge — please retry.')
          }
          bridgeData = resub.data
          localTransferId = bridgeData.transferId
          setState(prev => ({
            ...prev,
            transferId: bridgeData.transferId,
            orderId: bridgeData.orderId ?? null,
            bridgeResponse: bridgeData,
            currentStep: 'signing',
          }))
        }

        // Step 4a: the (possibly re-submitted) swap tx
        const tx = bridgeData.tx as { to?: string; data?: string; value?: string } | undefined
        if (!tx?.to || !tx?.data) {
          throw new Error('Missing transaction data from bridge response')
        }

        // Same-chain deBridge swaps carry no order to track (orderId is null); cross-chain
        // swaps do. Only same-chain routes through the DeBridgeRouter + stale-quote handling.
        const isSameChainSwap = !bridgeData.orderId

        // The give amount we approve the router to pull. Set in the ERC-20 approval block below;
        // re-validated against the rebuilt quote (same-chain staleness) so we never broadcast a tx
        // whose give exceeds the bounded approval. Null for native source (no approval).
        let approveTarget: bigint | null = null

        // Safety: the swap target is API-sourced — assert it against the allowlist for this route type.
        if (isSameChainSwap) assertSameChainRouter(tx.to)
        else assertCrossChainSpender(tx.to)

        // ── I-2 (fund-adjacent): refuse an unresolvable source chain BEFORE switch/approve/send ──
        // `EVM_CHAIN_IDS` is network-partitioned (config/chainSets.ts), so a chain that exists on
        // mainnet but not on this build resolves to `undefined` here. Left unguarded that flows into
        // `sendTransactionAsync({ chainId: undefined })`, and wagmi then passes `chain: null` to viem
        // (@wagmi/core sendTransaction.js:21), which SKIPS assertCurrentChain entirely
        // (viem actions/wallet/sendTransaction.js:93-100) — i.e. the tx is signed and broadcast on
        // whatever chain the wallet happens to be on. It also silently skipped the switchChain and
        // approval steps below. Mirrors the deposit-path guard at :987.
        //
        // Placement is deliberate: HERE, not where `evmChainId` is computed (:643). Moving it up
        // would also pre-empt the needsApproval block above, changing that path's user-visible error
        // from "Approval required but token/route info is missing" to this one. Tron returns at :591
        // and Solana at :637, so everything from :640 is EVM-only and this point is early enough.
        if (!evmChainId) throw new Error(`Unsupported source chain: ${params.sourceChain}`)

        if (evmChainId) {
          await switchChain(wagmiConfig, { chainId: evmChainId })
        }

        // Step 4b: ERC-20 approval for the swap tx's spender (no-op if B3 already approved enough).
        if (tokenInfo && !isNativeToken && evmChainId) {
          const decimals = tokenInfo.decimals ?? 6
          // Approve EXACTLY the give the router will transferFrom — the backend-surfaced approvalAmount
          // (input for same-chain; input + operating expense for cross-chain). Fall back to the input
          // only if absent (same-chain give == input). Re-checked against the rebuilt quote below.
          approveTarget = bridgeData.approvalAmount ? BigInt(bridgeData.approvalAmount) : parseUnits(params.amount, decimals)
          await approveErc20Exact(tokenInfo.address as `0x${string}`, tx.to as `0x${string}`, approveTarget, evmChainId)
        }

        // The tx we will actually broadcast and its backend transferId. Same-chain swaps
        // embed a short-lived aggregator quote; we rebuild (fresh quote, same fixed router)
        // when it goes stale or trips an empty revert, so the binding min isn't blown by
        // build→sign price drift. Cross-chain swaps never rebuild (these stay as-is).
        // gasLimit is deBridge's own estimate for this swap tx (same-chain only), as published by the
        // backend. deBridge sizes it APPROXIMATELY, not exactly: the rh2 revert consumed 463,185 of a
        // 469,944 limit (98.6%), so a heavy aggregator route can exceed it and revert out-of-gas
        // (#261 F6). The backend therefore MAY publish a buffered value — raw by default, and
        // deBridge's estimate scaled by DEX_SAME_CHAIN_GAS_BUFFER_PCT and capped when that flag is set.
        // Either way we apply what the backend published as-is; it is a ceiling (unused gas is refunded
        // on SUCCESS), and absent (cross-chain) → undefined → the wallet estimates as before.
        let activeTx: { to: string; data: string; value?: string; gasLimit?: string } =
          { to: tx.to, data: tx.data, value: tx.value, gasLimit: bridgeData.gas?.gasLimit }
        let activeTransferId = bridgeData.transferId

        const rebuildSameChainTx = async (): Promise<void> => {
          const fresh = await dexApi.submitBridge({
            sourceChain: params.sourceChain,
            destChain: params.destChain,
            sourceToken: params.sourceToken,
            destToken: params.destToken,
            amount: params.amount,
            recipientAddress: params.recipientAddress,
            senderAddress: params.senderAddress,
            senderNativeBalance: params.senderNativeBalanceWei,
            senderTokenBalance: params.senderTokenBalanceAtomic,
            ref: localStorage.getItem('parsebridge:ref') || undefined,
          })
          if (!fresh.success || !fresh.data?.tx) {
            throw new Error(fresh.error?.message || 'Failed to refresh swap quote')
          }
          const ftx = fresh.data.tx as { to?: string; data?: string; value?: string }
          if (!ftx.to || !ftx.data) {
            throw new Error('Refreshed swap quote missing transaction data')
          }
          assertSameChainRouter(ftx.to)
          // Re-validate the give for the rebuilt quote: the bounded approval was granted for the
          // original give, so refuse to broadcast if the fresh quote would pull more than approved.
          // (Same-chain give == input, which is invariant across rebuilds, so this never trips in
          // practice — it's a guard, not a re-approval round-trip.)
          if (approveTarget != null && fresh.data.approvalAmount && BigInt(fresh.data.approvalAmount) > approveTarget) {
            throw new Error('Refreshed quote requires more allowance than approved — re-initiate the swap.')
          }
          activeTx = { to: ftx.to, data: ftx.data, value: ftx.value, gasLimit: fresh.data.gas?.gasLimit }
          activeTransferId = fresh.data.transferId
          localTransferId = fresh.data.transferId
          quoteBuiltAt = Date.now()
        }

        const sendActiveTx = (): Promise<`0x${string}`> => sendTransactionAsync({
          to: activeTx.to as `0x${string}`,
          data: activeTx.data as `0x${string}`,
          value: activeTx.value ? BigInt(activeTx.value) : 0n,
          chainId: evmChainId,
          // Apply deBridge's gas limit when provided (same-chain swaps). Fee pricing
          // (maxFeePerGas/priority) is intentionally left to the wallet — only the limit is forced.
          ...(activeTx.gasLimit ? { gas: BigInt(activeTx.gasLimit) } : {}),
        })

        // Step 4c: Sign EVM swap transaction
        setState(prev => ({ ...prev, currentStep: 'signing' }))

        // If approval (or any delay) staled the same-chain quote, refresh it before sending.
        if (isSameChainSwap && Date.now() - quoteBuiltAt > SAME_CHAIN_QUOTE_STALE_MS) {
          await rebuildSameChainTx()
        }

        let txHash: `0x${string}`
        try {
          txHash = await sendActiveTx()
        } catch (sendErr) {
          // Same-chain empty revert = stale aggregator min (DeBridgeRouter swallows the inner
          // ReturnAmountIsNotEnough into a bare revert). Re-quote once and resend.
          if (isSameChainSwap && !isUserRejection(sendErr) && isEmptyRevert(sendErr)) {
            await rebuildSameChainTx()
            txHash = await sendActiveTx()
          } else {
            throw sendErr
          }
        }

        // Persist order only after broadcast — matches CCTP pattern where
        // transfers only appear in Activity after on-chain commitment.
        persistOrder(params, activeTransferId, bridgeData.orderId)

        localSourceTxHash = txHash
        setState(prev => ({
          ...prev,
          transferId: activeTransferId,
          sourceTxHash: txHash,
          currentStep: 'broadcasting',
        }))

        updateDexOrder(activeTransferId, {
          sourceTxHash: txHash,
          orderStatus: 'signed',
        })

        // Step 5: Confirm with backend
        await dexApi.confirmBridge(activeTransferId, txHash)

        // Step 6: Poll to a TERMINAL state (#94 Part B). Same-chain (no deBridge orderId) used to mark
        // 'complete' right here on broadcast — but a same-chain swap is one atomic tx that can REVERT, so that
        // reported reverted swaps as successful. Now the backend leaves it 'confirmed' and its verify pass
        // terminalizes once the source-tx receipt is mined; getOrderStatus maps that to Fulfilled/OrderCancelled,
        // which the poll loop resolves to complete/failed. So both same- and cross-chain poll (an honest
        // "confirming…" for the ~block-time window, then a truthful terminal — no false success).
        updateDexOrder(activeTransferId, { orderStatus: 'polling' })
        setState(prev => ({ ...prev, currentStep: 'polling' }))

        return txHash
      } catch (e) {
        if (isUserRejection(e)) {
          setState(prev => ({ ...prev, currentStep: 'idle', error: null }))
          return ''
        }

        const rawMsg = getErrorMessage(e)
        // #261 F2: `sourceChain` is threaded so the -32600 branch can name Robinhood ONLY on
        // Robinhood and stay generic elsewhere (it also makes the insufficient-native copy
        // chain-correct on this path, which it never was). Tron keeps its own classifier.
        const errorMsg = params.sourceChain === 'tron' ? classifyTronError(rawMsg) : getDisplayError(e, params.sourceChain)
        // #261 F9 site 2 — the phase to attribute this failure to. Computed ONCE and used for BOTH
        // the state write and the storage write, so the live card and the same order reopened after
        // a reload can never red different steps (which would be worse than the all-red it replaces,
        // because it would look authoritative).
        //
        // DERIVED from the plain locals, and NOT from `state` alone. `stateRef.current` is assigned on
        // every render (see its declaration), so it holds whatever React last COMMITTED — and React
        // does flush default-lane updates across the real network `await`s in this chain, so in
        // production it is usually current. "Usually" is not an invariant: nothing here can prove a
        // commit happened between a given `setState` and this catch, and the guarantee this code needs
        // is unconditional. That is what the `|| localTransferId` / `|| localSourceTxHash` disjuncts
        // provide — both plain `let`s assigned SYNCHRONOUSLY the moment their value is known, immune
        // to render timing by construction. They are the authoritative source; the `stateRef` read is
        // the convenience half. DO NOT REMOVE THE OR: on its own, a stale `stateRef` read attributes a
        // post-quote failure to 'fetching_quote', reddening a step that demonstrably succeeded.
        // (Reading `stateRef` inside the updater below is not an option either — that updater has not
        // run by the time the storage write two statements later needs the same value.)
        //
        // The derivation is exact at the resolution the panel actually has, in three tiers:
        //   source tx broadcast → 'polling'         (the confirm row)
        //   order id, no tx yet → 'signing'         (the "Sign & Broadcast" row)
        //   neither             → 'fetching_quote'  (the "Fetching Quote" row)
        // The 'polling' tier matters because `dexApi.confirmBridge` is awaited AFTER the broadcast and
        // after the hash is persisted: a 5xx there is downstream of a transaction that is already on
        // chain, and the sign row renders that tx's own explorer link — so reddening it would call a
        // mined transaction a failure. Every phase between the order id and the broadcast
        // ('approving' | 'signing' | 'broadcasting') renders on the SAME "Sign & Broadcast" row, so
        // 'signing' covers all three; everything before it renders on the quote row.
        //
        // This is inside the `catch` of the linear await chain and is deliberately NOT guarded — the
        // error write here is unconditional today and must stay that way (falsifier F-B applies to
        // sites 1 and 3 only).
        const failedPhase: DexBridgeStep =
          (stateRef.current.sourceTxHash || localSourceTxHash) ? 'polling'
          : (stateRef.current.transferId || localTransferId) ? 'signing'
          : 'fetching_quote'
        setState(prev => ({
          ...prev,
          currentStep: 'error',
          error: errorMsg,
          failedStep: failedPhase,
          // DEV-375 finding #1 — every failure reaching THIS catch is client-side: a broadcast that
          // threw, a receipt read that could not resolve (including receipt-unknown, which
          // `crossChainStep` still routes to 'confirming' via its own marker check), or a pre-broadcast
          // quote/sign failure. None of these are a backend-reported downstream cancel/refund, so this
          // is always 'source' — never 'downstream'.
          failedLeg: 'source',
        }))

        // ── I-4: tell the backend this swap failed (best-effort, never blocking) ────────────────
        // Until now a client-side swap failure was invisible to ops: `error_logs` has zero
        // references in apps/web/src and there is no Sentry/PostHog. This POST is the only seam.
        //
        // FIRE-AND-FORGET BY CONSTRUCTION: `void fetch(...).catch(() => {})`. Never awaited, so a
        // slow or dead endpoint cannot delay the error UI, and a rejected promise cannot become an
        // unhandled rejection or change the `throw e` below. A logging failure must be invisible.
        //
        // ⚠️ NEVER `errorMsg` DIRECTLY, AND NEVER `getErrorMessage(e)` / `e.message`. `errorMsg` is
        // NOT always the classified string above: the Tron branch is `classifyTronError(rawMsg)`,
        // which returns its input VERBATIM whenever none of its 3 known substrings match (#261 F2
        // A1) — i.e. the RAW provider text, address and all, reaches `state.error` unfiltered on
        // Tron. That is intentional for the on-screen message (unchanged by this fix) but must never
        // be what gets POSTed. So the POST deliberately RE-DERIVES its own message from the classifier
        // directly — `getReportDisplayError(e, params.sourceChain)`, which Tron never bypasses — and
        // then redacts any address-shaped run as a second, independent layer. A raw viem `.message` is
        // separately dangerous: a multi-line blob whose `Request Arguments:` section carries the
        // user's `from` address and calldata, and whose `Details:` tail carries the provider's raw
        // text — `getDisplayError`/`getReportDisplayError` never surface that (see the UNKNOWN-branch
        // handling in errors.ts). The endpoint independently re-screens `message` too (routes/
        // client-error.ts, redact-not-reject, with a >=64-hex tx-hash carve-out), but this FE
        // redaction is the first line, not the only one.
        //
        // ── P1 fix (#261 F2 GATE-4/S1) ─────────────────────────────────────────────────────────────
        // `getDisplayError()` truncates its UNKNOWN branch to 100 chars BEFORE this used to redact —
        // an address straddling that cut survived as a sub-40-char hex fragment the old regex could
        // not see (proved: an address starting at char 60 truncates to `0x` + 38 hex, two chars short
        // of the 40 the old regex required). `getReportDisplayError` is byte-identical to
        // `getDisplayError` for every tag except UNKNOWN, where it returns the classified text BEFORE
        // that truncation — so redaction below runs before any length bound, never after.
        //
        // ── P2 fix (defence in depth — the endpoint above independently re-screens `message` too) ──
        // Redaction (`redactAddressesForReport`, utils/errors.ts) mirrors the server's boundary-safe
        // pattern (routes/client-error.ts) — an EVM pass, a base58 pass for Tron/Solana addresses, a
        // >=64-hex tx-hash carve-out, and (GATE-4 G1) a structural once-per-run decision for a captured
        // hex run: ABI padding is redacted WHOLE, never handed to the base58 pass alone.
        //
        // ── P3 fix ──────────────────────────────────────────────────────────────────────────────────
        // `code` now comes from `getErrorCodeDeep`, which additionally walks `.cause` (bounded 10
        // hops, same cap as isInvalidRequestRpcError's fallback). `getErrorCode` alone returns
        // `undefined` for the real Robinhood shape (TransactionExecutionError wrapping
        // InvalidRequestRpcError): -32600 lives on `.cause`, not a top-level `.code`, so `error_code`
        // was NULL for exactly the one error this table exists to diagnose.
        // Plain `fetch`, not services/api.ts: the endpoint is public (its X-API-Key header would be
        // dead weight) and answers 204 with no body (fetchApi's envelope parsing has nothing to
        // parse). The classifier itself lives in utils/errors.ts, which has ZERO imports by design —
        // so the POST belongs here, never there.
        //
        // The redaction pass itself (`redactAddressesForReport`) lives in utils/errors.ts, not inline
        // here — extracted so it is directly unit-testable (GATE-4 finding G1). See that export's own
        // comment for the EVM/base58/hex-run/ABI-padded derivation this used to re-derive per render.
        const errorCode = getErrorCodeDeep(e)
        const clientErrorMessage = redactAddressesForReport(getReportDisplayError(e, params.sourceChain))
        void fetch('/api/client-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: clientErrorMessage,
            tag: getErrorTag(e),
            code: errorCode != null ? String(errorCode) : undefined,
            sourceChain: params.sourceChain,
            destChain: params.destChain,
            sourceToken: params.sourceToken,
            destToken: params.destToken,
            step: stateRef.current.currentStep,
          }),
        }).catch(() => {})

        const tidToUpdate = stateRef.current.transferId || localTransferId
        if (tidToUpdate) {
          // ── I10 (DEV-375). THE MOST DANGEROUS LINE IN THIS FIX, so read the whole note. ────────
          // This write was unconditionally `orderStatus:'failed'`, and it was harmless ONLY by
          // accident: `updateDexOrder` returns null for an ABSENT order (dexOrderStorage.ts:138-143),
          // and on the 1Click path nothing was persisted until after the receipt read, so on a
          // receipt failure there was no row for it to land on. The F5 hoist above persists at
          // BROADCAST — which makes this write LAND.
          //
          // 'failed' is TERMINAL: `selectReconcilableOrders` excludes terminal orders
          // (reconcileDexOrders.ts:31), so the background poll would never look at the order again.
          // A transient "we couldn't read the receipt" would become a PERMANENT "Failed" in Activity
          // that the 1Click fill can never correct — strictly worse than the bug being fixed.
          //
          // So a receipt-unknown stays NON-TERMINAL. 'polling' is the right non-terminal status
          // rather than an invented one: the hash is on chain, the order is exactly what the poller
          // is for, and `DexOrderStatus` has no 'unknown' member (dexOrderStorage.ts is outside this
          // change). `failedStep` is deliberately NOT written — writing it while the order is
          // non-terminal is precisely the stale-`failedStep` shape that makes a later 'complete' row
          // reopen with a red step (steps.ts's GATE-4 F-1 note).
          // DEV-375 finding #7 — this write was unconditional, and the SAME F5 hoist that makes it
          // land at all (see the note above) also makes it possible for it to land AFTER
          // `reconcileDexOrders`/the in-session poll has already written a TERMINAL 'complete' or
          // 'failed' row for this transferId — demoting an already-settled order back to a
          // non-terminal or differently-labelled failure. Guard it exactly like the monotonic
          // React-state guards already in this file (`:284`, `:296`, `:1616`), just against the
          // STORED order instead of `state.currentStep`.
          let existingOrder: StoredDexOrder | null = null
          try {
            existingOrder = getDexOrder(tidToUpdate)
          } catch {
            // `getDexOrder` already catches its own storage errors internally and returns null on
            // failure — this second catch exists only so the guard degrades to the PRE-#7 behaviour
            // (proceed with the write) rather than turning "we could not check" into a dropped write,
            // should this ever run against something that doesn't implement the call.
          }
          if (!existingOrder || (existingOrder.orderStatus !== 'complete' && existingOrder.orderStatus !== 'failed')) {
            const receiptUnknown = isReceiptUnknownMessage(errorMsg)
            updateDexOrder(tidToUpdate, receiptUnknown
              ? { orderStatus: 'polling', error: errorMsg }
              : {
                  orderStatus: 'failed',
                  error: errorMsg,
                  failedStep: failedPhase,
                })
          }
        }

        throw e
      }
    },
    [sendTransactionAsync, solanaSignTransaction, solanaConnection, persistOrder, signTronTransaction, tronAddress],
  )

  /**
   * DEV-375 — the Solana half of "a post-broadcast read failure is UNKNOWN, never failed", and of its
   * dual, "a real on-chain failure is never softened".
   *
   * ── WHY THIS EXISTS: `confirmTransaction` RESOLVES ON FAILURE ────────────────────────────────────
   * `@solana/web3.js`'s `confirmTransaction` does NOT throw when the transaction failed on chain. It
   * RESOLVES with `{ context, value: { err } }`, and all three call sites in this file discarded that
   * return value — so a Solana transfer that reverted on chain rendered as DELIVERED. That is the
   * inverse of the ticket's headline bug and sits on the same three lines, which is why it is fixed
   * in the same pass rather than filed.
   *
   * THE THREE-WAY RULE, and it is exactly three:
   *   · resolves, `err == null`  ⇒ SUCCESS  — proceed
   *   · resolves, `err != null`  ⇒ FAILED   — a terminal, on-chain failure; say so plainly
   *   · throws                   ⇒ UNKNOWN  — we never learned the outcome; the money may have moved
   *
   * The UNKNOWN copy is the SHARED `receiptUnknownMessage`, not a Solana-specific string: it carries
   * `RECEIPT_UNKNOWN_MARKER`, which is what exempts it from `classifyError`'s 100-char UNKNOWN
   * truncation — the truncation that was cutting the signature and the "don't submit it twice"
   * warning off the end of exactly this class of message.
   */
  async function confirmSolanaOrUnknown(signature: string, what: string): Promise<void> {
    let result: unknown
    try {
      result = await solanaConnection.confirmTransaction(signature, 'confirmed')
    } catch {
      // Every throw is "we could not obtain a confirmation" — an RPC/websocket/timeout failure tells
      // us nothing about the transaction, which may well have landed.
      throw new Error(receiptUnknownMessage(what, signature))
    }
    // `?? null` deliberately: a mocked or older client that resolves `undefined`/`{}` carries no
    // failure signal, and inventing one from its absence would be the I2 breach in the other
    // direction. Only an explicitly non-null `err` is a failure.
    const err = (result as { value?: { err?: unknown } } | null | undefined)?.value?.err ?? null
    if (err !== null) {
      // Terminal and on-chain: this one really did fail, so it must NOT carry the unknown marker.
      // DEV-375 finding #7: an 88-char base58 signature pushed this string to 116-128 chars with no
      // RECEIPT_UNKNOWN_MARKER, so `classifyError`'s 100-char truncation cut the signature mid-string
      // — unusable on an explorer, and easy to mistake for a deliberate abbreviation. The row already
      // links the signature, so the message doesn't need to repeat it.
      throw new Error(`The ${what} failed on-chain.`)
    }
  }

  /**
   * NEAR 1Click deposit: transfer the input token to the returned deposit address (plain transfer —
   * NO approve, NO calldata). Dispatches by source VM.
   *
   * CORRECTED (DEV-375): the line here used to read "Used only on the flag-gated Tron corridor". That
   * has not been true since near_1click became a first-class provider with cross-chain corridors
   * seeded to it (`apps/api/src/config/index.ts:884-896`) — this path carries ordinary production
   * traffic on base / optimism / arbitrum / bnb, which is exactly the set whose configured RPC gates
   * `eth_getTransactionReceipt`. Anyone sizing a change from the old sentence would have concluded
   * this was a flag-gated edge case.
   *
   * NOTE (Phase A): EVM-source is the verified corridor (Base USDC → Tron USDT). The Solana-source and
   * Tron-source send paths mirror existing wallet primitives but have NOT been live-settled — the
   * funded mainnet settlement test is a human step.
   */
  async function transferToNear1ClickDeposit(
    params: DexBridgeParams,
    depositAddress: string,
    _depositMemo: string | null,
    /**
     * DEV-375 F5 — fired SYNCHRONOUSLY at the broadcast point, before any post-broadcast read.
     *
     * The read that can fail lives inside this function while the caller holds no hash until it
     * returns, so this is a HOIST, not a reorder. Fired on the Solana and EVM branches only: Tron
     * performs no post-broadcast read here and returns its hash intact.
     */
    onBroadcast?: (hash: string) => void,
  ): Promise<string> {
    const { sourceChain, sourceToken, amount, senderAddress } = params

    // ── Tron source — native TRX (#599) OR a TRC-20 token; ONE sign → broadcast tail ──
    if (sourceChain === 'tron') {
      if (!tronAddress) throw new Error('Tron wallet not connected')
      let unsigned: Parameters<typeof signTronTransaction>[0]
      if (sourceToken === 'TRX') {
        // native TRX: a plain TransferContract to the deposit address — NOT a TRC-20 call. TRX is Tron's
        // only native asset and has no TRON_TOKEN_ADDRESSES row (the "Unsupported Tron token" throw was
        // the 2026-09-08 beta failure). Same OFF-by-default beta flag as the other native arms (I6): a
        // native deposit MOVES real value.
        if (import.meta.env.VITE_ENABLE_NATIVE_1CLICK_DEPOSIT !== 'true') {
          throw new Error('Native-source deposits are not yet enabled.')
        }
        const amountSun = parseUnits(amount, 6) // TRX is 6-dec (SUN)
        if (amountSun <= 0n) throw new Error('Amount must be greater than zero.')
        // tronweb parseInt()s the amount: above 2^53 SUN it would silently send a rounded value. REJECT, never clamp.
        if (amountSun > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Amount too large for a native TRX transfer.')
        const built = await tronWeb.transactionBuilder.sendTrx(depositAddress, Number(amountSun), tronAddress)
        if (!built?.txID) throw new Error('Failed to build Tron native deposit')
        // Re-verify the built transfer before signing: a plain TransferContract to the deposit address for
        // exactly the SUN requested. HONEST BOUND (GATE-4 round-2 N4): tronweb 6.3.0 constructs to_address
        // and amount CLIENT-SIDE from our own arguments (only the ref-block params come from the node), so
        // today this re-checks our construction, not the node's reply — it cannot fail in prod. It is kept
        // as forward cover for a tronweb major bump or an injected window.tronWeb (5.x builds server-side).
        const c = (built as { raw_data?: { contract?: Array<{ type?: string; parameter?: { value?: { to_address?: string; amount?: number } } }> } }).raw_data?.contract?.[0]
        const v = c?.parameter?.value
        if (c?.type !== 'TransferContract' || !v || v.to_address !== tronWeb.address.toHex(depositAddress) || v.amount !== Number(amountSun)) {
          throw new Error('Refusing to sign — the Tron node returned a transfer that does not match the request.')
        }
        unsigned = built
      } else {
        const tokenAddr = TRON_TOKEN_ADDRESSES[sourceToken]
        if (!tokenAddr) throw new Error(`Unsupported Tron token: ${sourceToken}`)
        const decimals = 6 // Tron USDT/USDC are 6-dec
        const amountAtomic = parseUnits(amount, decimals).toString()
        const senderHex41 = tronWeb.address.toHex(tronAddress)
        const built = await tronWeb.transactionBuilder.triggerSmartContract(
          tronWeb.address.toHex(tokenAddr),
          'transfer(address,uint256)',
          { feeLimit: 150_000_000 },
          [
            { type: 'address', value: depositAddress },
            { type: 'uint256', value: amountAtomic },
          ],
          senderHex41,
        )
        if (!built?.result?.result) throw new Error('Failed to build Tron deposit transfer')
        unsigned = built.transaction
      }
      const signed = await signTronTransaction(unsigned)
      setState(prev => ({ ...prev, currentStep: 'broadcasting' }))
      const receipt = await tronWeb.trx.sendRawTransaction(signed)
      if (!receipt.result) {
        throw new Error(typeof receipt.message === 'string' ? receipt.message : 'Tron broadcast failed')
      }
      return receipt.txid as string
    }

    // ── Solana source — native SOL (#599) OR an SPL token; ONE blockhash → sign → send → confirm tail ──
    if (sourceChain.includes('solana')) {
      if (!solanaSignTransaction) throw new Error('Solana wallet not connected')
      const tokenInfo = FALLBACK_TOKENS.find(t => t.symbol === sourceToken && t.chainName === 'solana')
      if (!tokenInfo) throw new Error(`Unsupported Solana token: ${sourceToken}`)
      const owner = new PublicKey(senderAddress)
      const dest = new PublicKey(depositAddress)
      const amountAtomic = parseUnits(amount, tokenInfo.decimals)
      const tx = new Transaction()

      if (tokenInfo.type === 'native') {
        // native SOL: a plain SystemProgram transfer to the deposit address — NOT an SPL transfer. The
        // registry's SOL row carries the wrapped-SOL mint as its address; transferChecked against it fails
        // in Phantom simulation for a wallet holding native lamports (the 2026-09-08 beta failure). Same
        // OFF-by-default beta flag as the EVM native arm (I6): a native deposit MOVES real value.
        if (import.meta.env.VITE_ENABLE_NATIVE_1CLICK_DEPOSIT !== 'true') {
          throw new Error('Native-source deposits are not yet enabled.')
        }
        // The deposit address must be a WALLET: a system-owned account, or one that does not exist yet.
        // ANY program-owned account — a classic SPL token account, a Token-2022 token account, a PDA —
        // receives lamports it can never forward, so it is refused. Read the raw account (getAccountInfo)
        // rather than spl-token's getAccount, which recognises only the classic token program and would
        // classify a Token-2022 account as "not a token account" (review findings A4/F-06). An RPC
        // failure throws and fails closed.
        const info = await solanaConnection.getAccountInfo(dest)
        if (info && !info.owner.equals(SystemProgram.programId)) {
          throw new Error('Refusing to deposit — deposit address is a program-owned account (token account or PDA), not a wallet.')
        }
        // Rent: a transfer that CREATES the account, or tops up a rent-PAYING one, must leave it rent-exempt
        // or the runtime rejects the transaction (a RentPaying→RentPaying transition with more lamports is
        // forbidden); an already rent-exempt account takes no floor (review finding F-05).
        const rent = BigInt(await solanaConnection.getMinimumBalanceForRentExemption(0))
        const pre = BigInt(info?.lamports ?? 0)
        if (pre < rent && pre + amountAtomic < rent) {
          throw new Error('Amount is below Solana’s rent-exempt minimum for the deposit account.')
        }
        tx.add(SystemProgram.transfer({ fromPubkey: owner, toPubkey: dest, lamports: amountAtomic }))
      } else {
        const mint = new PublicKey(tokenInfo.address)
        const sourceAta = await getAssociatedTokenAddress(mint, owner)

        // Resolve the 1Click deposit-address FORM (token account vs owner), mirroring the runner's
        // sendSplTransferTo (tier4-solana.ts). 1Click's Solana deposit address is usually a wallet OWNER
        // whose token account does NOT exist yet — a plain transfer to a non-existent ATA REVERTS in
        // simulation (Phantom's "transaction reverted during simulation"). So when it's an owner, we derive
        // its ATA and idempotently CREATE it in the same tx (the user pays ~0.002 SOL rent). If the deposit
        // address is itself a token account, we send to it directly. Fail-closed on a mint mismatch / RPC error.
        let destTokenAccount: PublicKey
        try {
          const acct = await getAccount(solanaConnection, dest)
          if (!acct.mint.equals(mint)) {
            throw new Error('Refusing to deposit — provider token account is for a different token.')
          }
          destTokenAccount = dest // it IS a token account → transfer directly, no create
        } catch (e) {
          if (e instanceof TokenAccountNotFoundError || e instanceof TokenInvalidAccountOwnerError) {
            // owner / nonexistent → derive its ATA and idempotently create it (off-curve allowed for PDAs).
            destTokenAccount = await getAssociatedTokenAddress(mint, dest, true)
            tx.add(createAssociatedTokenAccountIdempotentInstruction(owner, destTokenAccount, dest, mint))
          } else {
            throw e // real RPC failure or the mint-mismatch assertion — never silently fall through
          }
        }
        tx.add(createTransferCheckedInstruction(sourceAta, mint, destTokenAccount, owner, amountAtomic, tokenInfo.decimals))
      }
      const { blockhash } = await solanaConnection.getLatestBlockhash('confirmed')
      tx.feePayer = owner
      tx.recentBlockhash = blockhash
      const signed = await solanaSignTransaction(tx)
      setState(prev => ({ ...prev, currentStep: 'broadcasting' }))
      const txHash = await solanaConnection.sendRawTransaction(signed.serialize(), { skipPreflight: false, maxRetries: 3 })
      // F5 hoist — see onBroadcast's doc comment. Fired BEFORE the confirm, which is the read that
      // can fail.
      onBroadcast?.(txHash)
      await confirmSolanaOrUnknown(txHash, 'deposit transfer')
      return txHash
    }

    // ── EVM source (native ETH/… OR an ERC-20 stable on an EVM chain → …) ──
    // Native → send `value` to the deposit address (#185: the old code writeContract'd to the 0x0
    // sentinel → a no-op that never moved funds). ERC-20 → transfer(depositAddress, amt) to the token
    // contract. The two families must NEVER cross (the #185 bug crossed them).
    const evmChainId = EVM_CHAIN_IDS[sourceChain]
    if (!evmChainId) throw new Error(`Unsupported source chain: ${sourceChain}`)
    const tokenInfo = FALLBACK_TOKENS.find(t => t.symbol === sourceToken && t.chainName === sourceChain)
    if (!tokenInfo) throw new Error(`Unsupported token ${sourceToken} on ${sourceChain}`)
    const amountAtomic = parseUnits(amount, tokenInfo.decimals)
    const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
    // Native predicate: the fallback-tokens sentinel for a chain's native asset. tokenInfo is already
    // guaranteed non-null (the throw just above) — do NOT copy the `!tokenInfo || …` predicate at :647,
    // which would route a fallback-absent ERC-20 into the native branch.
    const isNativeSource = tokenInfo.address === ZERO_ADDRESS

    if (isNativeSource) {
      // M3 (0x0 guard, ALWAYS ON, flag-independent): the native family's ACTUAL tx.to is the deposit
      // address — never sign a value transfer to an empty/zero target. depositAddress is also validated
      // by isValidDepositAddress at the near_1click submit step (which since #599 rejects the 0x0 sentinel
      // itself, so this line is unreachable via the route); kept as defense-in-depth for the native tx.to.
      if (!depositAddress || depositAddress === ZERO_ADDRESS) {
        throw new Error('Refusing to deposit — empty or zero deposit address.')
      }
      // #185: a native-source deposit MOVES real value. Gated behind the OFF-by-default beta flag until a
      // funded settlement test confirms native 1Click settles from a plain value transfer. When OFF, fail
      // CLOSED with a clear message (NOT the old silent no-op, which kept funds but did nothing).
      if (import.meta.env.VITE_ENABLE_NATIVE_1CLICK_DEPOSIT !== 'true') {
        throw new Error('Native-source deposits are not yet enabled.')
      }
    } else {
      // M3 (0x0 guard, ERC-20 arm): the ERC-20 family's ACTUAL tx.to is the token contract — the #185
      // bug signed a transfer whose token address was the 0x0 sentinel. Defense-in-depth: the predicate
      // above already routes a 0x0 address to the native family, so this asserts that coupling holds.
      if (tokenInfo.address === ZERO_ADDRESS) {
        throw new Error('Refusing to deposit — token contract address is zero.')
      }
    }

    const TRANSFER_ABI = [{ name: 'transfer', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }] as const

    // Ensure the wallet is on the source chain before sending.
    try { await switchChain(wagmiConfig, { chainId: evmChainId }) } catch { /* may already be on-chain / unsupported */ }

    setState(prev => ({ ...prev, currentStep: 'broadcasting' }))
    const hash = isNativeSource
      ? await sendTransactionAsync({ to: depositAddress as `0x${string}`, value: amountAtomic, chainId: evmChainId })
      : await writeContract(wagmiConfig, {
          address: tokenInfo.address as `0x${string}`,
          abi: TRANSFER_ABI,
          functionName: 'transfer',
          args: [depositAddress as `0x${string}`, amountAtomic],
          chainId: evmChainId,
        })
    // F5 HOIST (DEV-375). The hash exists HERE and nowhere earlier — the caller has nothing until
    // this function returns — so the four post-broadcast writes are fired from the call site through
    // this callback rather than after the return. Without it a receipt-read failure below throws
    // with `localSourceTxHash` still null: `failedPhase` resolves to 'signing', the order never
    // reaches localStorage, and the copy's own "check the explorer for 0x…" instruction names a hash
    // the UI cannot show. Tron does NOT fire it (see the Tron branch) — it has no post-broadcast
    // read here, so its hash is already returned intact.
    onBroadcast?.(hash)
    // Bounded: @wagmi/core overrides viem's 180s default with `timeout = 0`, and viem treats 0 as
    // "install no timer", so an unresolvable hash (a Safe's safeTxHash is the real case) hangs
    // forever with no error branch ever engaging.
    //
    // DEV-375 — the predicate is now "did we obtain a receipt", not "was this a timeout". The old
    // inlined copy bounded ONE error name and rethrew everything else, so the measured `-32602`
    // archive gate on base / optimism / arbitrum / bnb fell straight through to a terminal
    // "Transfer Failed" on a deposit that had actually landed. Every non-obtain outcome is UNKNOWN;
    // a receipt that says `reverted` is still a real, terminal failure (I2).
    // DEV-375 finding #10 — `getClient` is resolved BEFORE the helper's own try, so a
    // `ChainNotConfiguredError` here would propagate raw and render "Transfer Failed" on a broadcast
    // (`onBroadcast` already ran above) that is on chain. Practically unreachable today (the chain
    // just signed a transaction on), but it would otherwise contradict the "the predicate is the
    // class, not a name list" claim the whole fix rests on — so it is folded into the same UNKNOWN
    // class as every other no-receipt outcome.
    let evmClient: unknown
    try {
      evmClient = wagmiConfig.getClient({ chainId: evmChainId })
    } catch {
      throw new Error(receiptUnknownMessage('deposit transfer', hash))
    }
    const receipt = await waitForReceiptOrUnknown(evmClient, {
      hash, timeout: RECEIPT_WAIT_TIMEOUT_MS, what: 'deposit transfer',
    })
    // DEV-375 finding #9 — `waitForReceiptOrUnknown` normalises a wallet cancel/replace to
    // status:'reverted' (erc20Approve.ts's I9 handling), and "reverted" is a claim about the CHAIN
    // executing and rejecting the transaction. A user who hit Cancel in their wallet never broadcast
    // anything that could revert — telling them it "reverted on chain" is a false, more alarming
    // statement than what actually happened. `replacedReason` distinguishes the two truths.
    if (receipt.status === 'reverted') {
      if (receipt.replacedReason === 'cancelled') {
        throw new Error('Deposit transfer was cancelled in your wallet before it reached the chain')
      }
      if (receipt.replacedReason === 'replaced') {
        throw new Error('Deposit transfer was replaced by another transaction in your wallet before it reached the chain')
      }
      throw new Error('Deposit transfer reverted')
    }
    // I9 — return the MINED hash, not the one we broadcast. viem resolves a replacement's receipt,
    // so on a repriced transaction the original hash is dead and persisting it would give the user
    // an explorer link to nothing. (`cancelled`/`replaced` never reach here: `waitForReceiptOrUnknown`
    // normalises those to 'reverted' above.)
    return receipt.transactionHash
  }

  /**
   * Sign and broadcast a Tron bridge transaction.
   *
   * Flow: TRC-20 approval (if needed) → build bridge tx via TronWeb → sign via wallet → broadcast → confirm
   *
   * Uses deBridge's tx.to/tx.data/tx.value fields with TronWeb's transactionBuilder.triggerSmartContract()
   * to construct Tron-native transactions, following the pattern from deBridge's official integrator example.
   */
  async function signAndBroadcastTron(params: DexBridgeParams, bridgeData: DexBridgeResponse): Promise<string> {
    if (!tronAddress) {
      throw new Error('Tron wallet not connected')
    }

    const tx = bridgeData.tx as { to?: string; data?: string; value?: string } | undefined
    if (!tx?.to || !tx?.data) {
      throw new Error('Missing transaction data from Tron bridge response')
    }

    const contractAddrHex = tx.to
    const callData = tx.data
    // For TRC-20 bridges, callValue comes from fixFee (native TRX for protocol fees).
    // For native TRX bridges, tx.value contains the bridge amount.
    const rawResponse = bridgeData as unknown as Record<string, unknown>
    const callValueRaw = String(tx.value || rawResponse.fixFee || '0')
    const callValueSun = Number(callValueRaw)
    if (!Number.isSafeInteger(callValueSun)) {
      throw new Error('Bridge call value exceeds safe integer range')
    }

    // Convert deBridge's 0x-prefixed contract address to Tron base58
    const debridgeRouterBase58 = contractAddrHex.startsWith('0x')
      ? tronWeb.address.fromHex(`41${contractAddrHex.slice(2)}`)
      : contractAddrHex

    // ── TRC-20 Approval ──────────────────────────────────────────
    const tokenAddr = TRON_TOKEN_ADDRESSES[params.sourceToken]
    if (tokenAddr) {
      // Check current allowance
      const contract = await tronWeb.contract().at(tokenAddr)
      const currentAllowance = BigInt(
        String(await contract.allowance(tronAddress, debridgeRouterBase58).call())
      )

      // Approve EXACTLY the give the router will transferFrom — the backend-surfaced approvalAmount
      // (input + cross-chain operating expense), falling back to the estimation give, then bare input.
      // NEVER max uint256: that grants unlimited standing TRC-20 spend authority.
      const estimation = (bridgeData as unknown as Record<string, unknown>).estimation as
        { srcChainTokenIn?: { amount?: string } } | undefined
      const requiredAmount = BigInt(bridgeData.approvalAmount ?? estimation?.srcChainTokenIn?.amount ?? params.amount)

      if (currentAllowance < requiredAmount) {
        setState(prev => ({ ...prev, currentStep: 'signing' }))

        const senderHex41 = tronWeb.address.toHex(tronAddress)
        const tokenHex41 = toTronHex41(tokenAddr)

        // Build + sign + broadcast one approve(spender, amount).
        const sendApprove = async (amount: string): Promise<void> => {
          const wrapper = await tronWeb.transactionBuilder.triggerSmartContract(
            tokenHex41,
            'approve(address,uint256)',
            { callValue: 0, feeLimit: 100_000_000 },
            [
              { type: 'address', value: debridgeRouterBase58 },
              { type: 'uint256', value: amount },
            ],
            senderHex41,
          )
          if (!wrapper?.result?.result) {
            throw new Error('Failed to build TRC-20 approval transaction')
          }
          const signed = await signTronTransaction(wrapper.transaction)
          const receipt = await tronWeb.trx.sendRawTransaction(signed)
          if (!receipt.result) {
            const errMsg = receipt.message
              ? (typeof receipt.message === 'string' ? receipt.message : 'Approval broadcast failed')
              : 'Approval broadcast failed'
            throw new Error(errMsg)
          }
        }

        // forceApprove semantics: TRC-20 USDT reverts on approve(nonzero) over a nonzero allowance —
        // reset to 0 first. Then approve exactly the give.
        if (currentAllowance > 0n) {
          await sendApprove('0')
        }
        await sendApprove(requiredAmount.toString())

        // Poll allowance until updated or timeout (10s, check every 2s)
        let approvalConfirmed = false
        const deadline = Date.now() + 10_000
        while (Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 2000))
          const updated = BigInt(
            String(await contract.allowance(tronAddress, debridgeRouterBase58).call())
          )
          if (updated >= requiredAmount) {
            approvalConfirmed = true
            break
          }
        }
        if (!approvalConfirmed) {
          throw new Error('Token approval not confirmed after 10s. Please try again.')
        }
      }
    }

    // ── Build & sign bridge transaction ──────────────────────────
    const contractHex41 = toTronHex41(contractAddrHex)
    const dataNo0x = clipHexPrefix(callData)
    const senderHex41 = tronWeb.address.toHex(tronAddress)

    const bridgeTxWrapper = await tronWeb.transactionBuilder.triggerSmartContract(
      contractHex41,
      '',
      { callValue: callValueSun, input: dataNo0x, feeLimit: 150_000_000 },
      [],
      senderHex41,
    )

    if (!bridgeTxWrapper?.result?.result) {
      throw new Error('Failed to build Tron bridge transaction')
    }

    const signedBridge = await signTronTransaction(bridgeTxWrapper.transaction)

    setState(prev => ({ ...prev, currentStep: 'broadcasting' }))

    // ── Broadcast ────────────────────────────────────────────────
    const receipt = await tronWeb.trx.sendRawTransaction(signedBridge)
    if (!receipt.result) {
      const errMsg = receipt.message
        ? (typeof receipt.message === 'string' ? receipt.message : 'Tron broadcast failed')
        : 'Tron broadcast failed'
      throw new Error(errMsg)
    }

    const txid = receipt.txid

    // Persist order only after broadcast — matches CCTP pattern where
    // transfers only appear in Activity after on-chain commitment.
    persistOrder(params, bridgeData.transferId, bridgeData.orderId)

    setState(prev => ({
      ...prev,
      sourceTxHash: txid,
    }))

    updateDexOrder(bridgeData.transferId, {
      sourceTxHash: txid,
      orderStatus: 'signed',
    })

    // ── Confirm with backend + start polling ─────────────────────
    await dexApi.confirmBridge(bridgeData.transferId, txid)
    updateDexOrder(bridgeData.transferId, { orderStatus: 'polling' })

    setState(prev => ({
      ...prev,
      currentStep: 'polling',
    }))

    return txid
  }

  const confirmRouteChange = useCallback(() => {
    // User confirmed the fee delta — proceed
    setState(prev => ({ ...prev, currentStep: 'fetching_quote' }))
  }, [])

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  // Dismiss the error banner (and the failed step panel) without wiping unrelated state. Used by the
  // auto-dismiss timer so a transient failure (e.g. UNECONOMICAL_ROUTE) doesn't linger forever.
  //
  // `failedStep` is cleared WITH the error, not left behind (I-7's spirit). This runs 15s after every
  // DEX failure (BridgePage's auto-dismiss effect), and it is the only writer that returns the hook to
  // a non-terminal step without going through `reset()`. A surviving `failedStep` would then be the
  // stale label of a swap that is no longer on screen, waiting for the next `setState(prev => …)` to
  // carry it into a brand-new swap — which would render as quote-done / sign-done / confirm-error
  // before that swap had done anything at all.
  const clearError = useCallback(() => {
    setState(prev => (prev.error || prev.currentStep === 'error')
      // DEV-375 finding #1 — `failedLeg` cleared alongside `failedStep`, same I-7 spirit: a surviving
      // 'downstream' would otherwise carry into a brand-new swap's `crossChainStep` mapping.
      ? { ...prev, error: null, currentStep: 'idle', failedStep: null, failedLeg: null }
      : prev)
  }, [])

  /**
   * Durable-reconcile bridge for the LIVE order. The in-session poll above only advances the live view
   * while this hook stays mounted with state intact — but a near_1click cross-chain fill can take minutes
   * (deposit → solve → our on-chain-credit verify guardrail), long enough that the tab is backgrounded or
   * fully suspended (mobile suspends JS timers, so even refetchIntervalInBackground can't fire) or the user
   * navigates. BridgePage's background reconcile loop DOES survive that (it re-polls persisted orders on an
   * interval) but only writes the terminal state to localStorage — it can't reach this hook's dexStep. This
   * lets it mirror the currently-tracked order's terminal into the live view so the progress panel flips to
   * complete/error and the spinner stops, exactly as the in-session poll would have — and, via the existing
   * dexStep→'complete' effect in BridgePage, refreshes balances + Activity.
   *
   * Guarded: no-op unless `transferId` matches the tracked order AND it is still 'polling'. That makes it
   * idempotent and race-safe — it can only drive the SAME forward transition the poll would (never
   * resurrects a reset/idle flow, never regresses, never fires when the in-session poll already terminalized).
   */
  const applyReconciledTerminal = useCallback((transferId: string, terminal: 'complete' | 'error'): void => {
    setState(prev => {
      if (prev.transferId !== transferId) return prev
      // DEV-375 finding #8 — widened from `prev.currentStep !== 'polling'` alone: a receipt-unknown
      // live view is `currentStep:'error'` (I10), and the ORIGINAL guard could never correct it once
      // the backend later reported the truth. That correction is real for failedLeg and storage, but
      // was NOT real for the rendered message until cycle 3's fix below — the marker text was sticky
      // through this transition in both directions. A genuinely failed/complete live view is
      // unaffected: `isReceiptUnknownMessage` is false for it, so this still cannot resurrect a
      // reset/idle flow or re-fire once truly terminal.
      const correctable = prev.currentStep === 'polling' ||
        (prev.currentStep === 'error' && isReceiptUnknownMessage(prev.error))
      if (!correctable) return prev
      // Mirror the in-session poll's terminal copy so a reconcile-driven failure isn't a blank error panel.
      return terminal === 'error'
        ? {
            ...prev,
            currentStep: 'error',
            // DEV-375 cycle 3 — a receipt-unknown marker message must not survive a backend-confirmed
            // terminal (I2): once reconciled, show the real terminal-failure message, not the marker.
            error: isReceiptUnknownMessage(prev.error)
              ? DEX_TERMINAL_FAILURE_MESSAGE
              : (prev.error ?? DEX_TERMINAL_FAILURE_MESSAGE),
            // #261 F9 site 3. The guard above proves the order was 'polling' or a receipt-unknown
            // tracking-phase 'error', so that is the phase that failed. No storage write here on
            // purpose: the reconcile pass that drives this already persisted the same value
            // (reconcileDexOrders.ts, sites 4/5) before calling in.
            failedStep: 'polling',
            // DEV-375 finding #1 — this function only ever runs from a BACKEND-reported terminal (the
            // reconcile pass read the order status from the API before calling in), so a
            // terminal:'error' here is always a downstream cancel/refund, never a source-side
            // broadcast/receipt failure.
            failedLeg: 'downstream',
          }
        : { ...prev, currentStep: 'complete', error: null, failedStep: null, failedLeg: null }
    })
  }, [])

  return {
    dexBridgeState: state,
    currentStep: state.currentStep,
    transferId: state.transferId,
    orderId: state.orderId,
    sourceTxHash: state.sourceTxHash,
    estimate: state.estimate,
    bridgeResponse: state.bridgeResponse,
    orderStatus: state.orderStatus,
    error: state.error,
    /** #261 F9 — the phase that failed, for the progress card's per-step rendering. */
    failedStep: state.failedStep,
    /** DEV-375 finding #1 — WHICH leg failed (source vs downstream), for the cross-chain mapping. */
    failedLeg: state.failedLeg,
    amount: state.amount,
    executeDexBridge,
    confirmRouteChange,
    reset,
    clearError,
    applyReconciledTerminal,
  }
}

/** Map Tron-specific error messages to user-friendly messages */
function classifyTronError(msg: string): string {
  const lower = msg.toLowerCase()
  if (lower.includes('energy_not_sufficient') || lower.includes('out_of_energy')) {
    return 'Not enough energy for this transaction. Stake TRX for energy or wait for recovery.'
  }
  if (lower.includes('bandwith_error') || lower.includes('bandwidth')) {
    return 'Not enough bandwidth for this transaction. Stake TRX for bandwidth or wait for recovery.'
  }
  if (lower.includes('contract_validate_error')) {
    return 'Transaction validation failed. The contract rejected this transaction.'
  }
  return msg
}
